import axios from "axios";

const UBIQUITI_API_TIMING_LIMIT = 120;
const UBIQUITI_SLOW_API_THRESHOLD_MS = 1000;

const getNow = () => (
  typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now()
);

const getResponseBytes = (response) => {
  const contentLength = response?.headers?.get?.("content-length") ?? response?.headers?.["content-length"];
  const bytes = Number(contentLength);

  return Number.isFinite(bytes) ? bytes : null;
};

const roundTimingValue = (value) => (
  Number.isFinite(value) ? Math.round(value) : null
);

const getBrowserResourceTiming = (url, startedAt) => {
  if (typeof performance === "undefined" || typeof performance.getEntriesByType !== "function") {
    return null;
  }

  const entries = performance.getEntriesByType("resource")
    .filter((entry) => (
      entry.name === url
      || entry.name.startsWith(`${url}?`)
    ))
    .filter((entry) => !Number.isFinite(startedAt) || entry.responseEnd >= startedAt - 5)
    .sort((first, second) => second.responseEnd - first.responseEnd);
  const entry = entries[0];

  if (!entry) {
    return null;
  }

  return {
    totalMs: roundTimingValue(entry.duration),
    blockedMs: entry.domainLookupStart > 0
      ? roundTimingValue(entry.domainLookupStart - entry.startTime)
      : null,
    stalledMs: entry.requestStart > 0
      ? roundTimingValue(entry.requestStart - entry.startTime)
      : null,
    waitingMs: entry.responseStart > 0 && entry.requestStart > 0
      ? roundTimingValue(entry.responseStart - entry.requestStart)
      : null,
    downloadMs: entry.responseEnd > 0 && entry.responseStart > 0
      ? roundTimingValue(entry.responseEnd - entry.responseStart)
      : null,
    transferSize: entry.transferSize || null,
    encodedBodySize: entry.encodedBodySize || null,
    decodedBodySize: entry.decodedBodySize || null,
  };
};

const recordUbiquitiApiTiming = ({ bytes, name, ok, path, startedAt, status, url, error }) => {
  if (typeof window === "undefined") {
    return;
  }

  if (typeof performance !== "undefined" && typeof performance.setResourceTimingBufferSize === "function") {
    performance.setResourceTimingBufferSize(300);
  }

  const durationMs = Math.round(getNow() - startedAt);
  const browserTiming = getBrowserResourceTiming(url, startedAt);
  const entry = {
    name,
    path: path.startsWith("/") ? path : `/${path}`,
    ok,
    status: status ?? null,
    durationMs,
    browserTiming,
    bytes: ok ? bytes ?? null : null,
    recordedAt: new Date().toISOString(),
    error: error || null,
  };
  const timings = Array.isArray(window.__ubiquitiApiTimings)
    ? window.__ubiquitiApiTimings
    : [];

  timings.push(entry);
  window.__ubiquitiApiTimings = timings.slice(-UBIQUITI_API_TIMING_LIMIT);
  window.__ubiquitiApiTimingSummary = [...window.__ubiquitiApiTimings]
    .sort((first, second) => second.durationMs - first.durationMs);

  if (name === "dashboard-graphs") {
    window.__ubiquitiDashboardGraphsTiming = entry;
  }

  if (import.meta.env.DEV && (name === "dashboard-graphs" || durationMs >= UBIQUITI_SLOW_API_THRESHOLD_MS)) {
    // console.info(`[Ubiquiti API] ${name}: ${durationMs}ms`, entry);
  }
};

const getUbiquiti = async (apiUrl, path, { name, params } = {}) => {
  const url = `${apiUrl}${path}`;
  const startedAt = getNow();

  try {
    const response = await axios.get(url, {
      withCredentials: true,
      ...(params ? { params } : {}),
    });

    recordUbiquitiApiTiming({
      name: name || path,
      ok: true,
      path,
      bytes: getResponseBytes(response),
      startedAt,
      status: response.status,
      url,
    });

    return response.data;
  } catch (requestError) {
    recordUbiquitiApiTiming({
      name: name || path,
      ok: false,
      path,
      startedAt,
      status: requestError.response?.status,
      url,
      error: requestError.message,
    });

    throw requestError;
  }
};

export const getUbiquitiSites = async (apiUrl) => {
  return getUbiquiti(apiUrl, "ubiquiti/sites", {
    name: "sites",
  });
};

export const getUbiquitiWifiDefaultSpeeds = async (apiUrl, siteId) => {
  return getUbiquiti(apiUrl, `ubiquiti/sites/${encodeURIComponent(siteId)}/wifi-default-speeds`, {
    name: "wifi-default-speeds",
  });
};

export const getUbiquitiGatewaySummary = async (apiUrl, siteId) => {
  return getUbiquiti(apiUrl, `ubiquiti/sites/${encodeURIComponent(siteId)}/gateway-summary`, {
    name: "gateway-summary",
  });
};

export const getUbiquitiInternetSummary = async (apiUrl, siteId) => {
  return getUbiquiti(apiUrl, `ubiquiti/sites/${encodeURIComponent(siteId)}/internet-summary`, {
    name: "internet-summary",
  });
};

export const getUbiquitiInternetLivePoint = async (apiUrl, siteId) => {
  return getUbiquiti(apiUrl, `ubiquiti/sites/${encodeURIComponent(siteId)}/internet-live-point`, {
    name: "internet-live-point",
  });
};

export const getUbiquitiDashboardGraphs = async (apiUrl, siteId) => {
  return getUbiquiti(apiUrl, `ubiquiti/sites/${encodeURIComponent(siteId)}/dashboard-graphs`, {
    name: "dashboard-graphs",
  });
};

export const getUbiquitiTopOverview = async (apiUrl, siteId) => {
  return getUbiquiti(apiUrl, `ubiquiti/sites/${encodeURIComponent(siteId)}/top-overview`, {
    name: "top-overview",
  });
};

export const getUbiquitiMostCommonDevices = async (apiUrl, siteId) => {
  return getUbiquiti(apiUrl, `ubiquiti/sites/${encodeURIComponent(siteId)}/most-common-devices`, {
    name: "most-common-devices",
  });
};
